// frontend/src/features/medications/components/MedicationDetailsDrawer.jsx

import React from "react";
import Badge from "../../../shared/ui/Badge";
import Button from "../../../shared/ui/Button";
import Card from "../../../shared/ui/Card";
import colors from "../../../shared/theme/colors";
import typography from "../../../shared/theme/typography";

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function toNumber(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const normalized = value.replace(",", ".").trim();
    if (!normalized) return null;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function pickFirstDefined(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return null;
}

function formatNumber(value) {
  const number = toNumber(value);
  if (number === null) return null;

  return new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 2,
  }).format(number);
}

function getPackageStatus(item) {
  const status = normalizeText(item?.status);

  if (["em_uso", "em uso", "uso"].includes(status)) {
    return { label: "Em uso", variant: "success" };
  }

  if (["disponivel", "em estoque", "ativo"].includes(status)) {
    return { label: "Disponível", variant: "success" };
  }

  if (["utilizado", "usado", "acabou", "esgotado"].includes(status)) {
    return { label: "Utilizada", variant: "neutral" };
  }

  if (["planejado"].includes(status)) {
    return { label: "Planejada", variant: "warning" };
  }

  return { label: "Embalagem", variant: "neutral" };
}

function getPackageName(item, index) {
  return (
    item?.label ||
    item?.packaging ||
    item?.packagingLabel ||
    item?.brand ||
    item?.packageName ||
    `Embalagem ${index + 1}`
  );
}

function getPackageAmount(item, medicationType) {
  const isUnit = medicationType === "COMPRIMIDO" || medicationType === "CAPSULA" || medicationType === "PO";
  const itemStatus = normalizeText(item?.status);
  const isUsedUp = ["utilizado", "usado", "acabou", "esgotado"].includes(itemStatus);

  if (isUnit) {
    let units = pickFirstDefined(
      toNumber(item?.remainingUnits),
      toNumber(item?.remaining_units),
      toNumber(item?.quantity),
      toNumber(item?.stockUnits)
    );

    if ((units === null || (units === 0 && !isUsedUp))) {
      const remMg = toNumber(item?.remaining_mg ?? item?.remainingMg ?? item?.total_mg_available ?? item?.totalMgAvailable);
      const mgPerUnit = toNumber(item?.mg_per_unit ?? item?.mgPerUnit ?? item?.mg_base ?? item?.mgBase);

      if (remMg !== null && remMg > 0 && mgPerUnit !== null && mgPerUnit > 0) {
        units = Math.floor(remMg / mgPerUnit);
      } else {
        const totUnits = toNumber(item?.total_units ?? item?.totalUnits);
        if (totUnits !== null && totUnits > 0) {
          units = totUnits;
        }
      }
    }

    if (units === null) return "Quantidade não informada";

    if (medicationType === "PO") {
      return `${formatNumber(units)} doses`;
    }

    return `${formatNumber(units)} ${
      medicationType === "CAPSULA" ? "cápsulas" : "comprimidos"
    }`;
  }

  let mg = pickFirstDefined(
    toNumber(item?.remaining_mg),
    toNumber(item?.remainingMg),
    toNumber(item?.availableMg),
    toNumber(item?.stockMg)
  );

  if ((mg === null || (mg === 0 && !isUsedUp))) {
    const totMg = pickFirstDefined(
      toNumber(item?.total_mg_available),
      toNumber(item?.totalMgAvailable)
    );
    if (totMg !== null && totMg > 0) {
      mg = totMg;
    }
  }

  if (mg === null) return "Quantidade não informada";

  return `${formatNumber(mg)} mg restantes`;
}

function getTypeIcon(type) {
  if (type === "INJETAVEL") return "💉";
  if (type === "CAPSULA") return "💊";
  if (type === "COMPRIMIDO") return "💊";
  if (type === "LIQUIDO") return "🧴";
  if (type === "PO") return "🥤";
  return "💊";
}

function getId(item) {
  return item?.id || item?._id || item?.flaskId || item?.flask_id;
}

function canEditPackage(item) {
  const status = normalizeText(item?.status);
  return ["planejado", "disponivel", "em estoque", "ativo"].includes(status);
}

function canDeletePackage(item) {
  const status = normalizeText(item?.status);
  return !["utilizado", "usado"].includes(status);
}

export default function MedicationDetailsDrawer({
  medication,
  isOpen,
  onClose,
  onAddPackage,
  onEditPackage,
  onUpdatePackageStatus,
  onDeletePackage,
}) {
  if (!isOpen || !medication) return null;

  const packages = Array.isArray(medication.packagingItems)
    ? medication.packagingItems
    : [];

  const icon = getTypeIcon(medication.medicationType);

  // Badge de status geral da medicação no cabeçalho
  const overallStatus = medication.status;
  let headerBadgeVariant = "success";
  let headerBadgeLabel = "Disponível";

  if (overallStatus === "acabou") {
    headerBadgeVariant = "error";
    headerBadgeLabel = "Sem estoque";
  } else if (overallStatus === "acabando") {
    headerBadgeVariant = "warning";
    headerBadgeLabel = "Atenção";
  } else if (overallStatus === "disponivel") {
    headerBadgeVariant = "success";
    headerBadgeLabel = "Disponível";
  } else if (overallStatus === "inativo") {
    headerBadgeVariant = "neutral";
    headerBadgeLabel = "Inativa";
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999,
        background: "rgba(47, 37, 69, 0.45)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          maxHeight: "85vh",
          overflowY: "auto",
          borderRadius: 28,
          background: colors.surface,
          padding: "20px 20px 24px",
          boxShadow: "0 20px 50px rgba(36,21,47,0.25)",
          border: `1px solid ${colors.border}`,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        {/* CABEÇALHO LIMPO CENTRALIZADO COM BADGE ENTRE NOME E FECHAR */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 18,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 16,
                background: "#F4F0FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 26,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              {icon}
            </div>

            <div style={{ minWidth: 0 }}>
              <h3
                style={{
                  margin: 0,
                  fontFamily: typography.fontFamily,
                  fontSize: typography.size.lg,
                  fontWeight: typography.weight.extrabold,
                  color: colors.primaryDark,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {medication.name || "Medicação"}
              </h3>

              <p
                style={{
                  margin: "2px 0 0",
                  fontFamily: typography.fontFamily,
                  fontSize: typography.size.sm,
                  color: colors.textSecondary,
                  fontWeight: typography.weight.medium,
                }}
              >
                {medication.availableTotalLabel || "—"} disponíveis
              </p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <Badge variant={headerBadgeVariant}>{headerBadgeLabel}</Badge>

            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar detalhes"
              style={{
                border: "none",
                background: "transparent",
                fontSize: 22,
                cursor: "pointer",
                color: colors.textSecondary,
                padding: "2px 6px",
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* BOTÃO PRINCIPAL DE ADICIONAR EMBALAGEM */}
        <Button
          variant="primary"
          fullWidth
          onClick={() => onAddPackage?.(medication)}
          style={{ marginBottom: 14 }}
        >
          + Adicionar embalagem
        </Button>

        {/* LISTA DE EMBALAGENS */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            width: "100%",
          }}
        >
          {packages.length === 0 ? (
            <Card padding={12} style={{ width: "100%" }}>
              Nenhuma embalagem encontrada para esta medicação.
            </Card>
          ) : (
            packages.map((item, index) => {
              const status = getPackageStatus(item);
              const id = getId(item);
              const isAvailableStatus = status.label === "Em estoque" || status.label === "Disponível";

              return (
                <Card
                  key={id || index}
                  padding={12}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxSizing: "border-box",
                  }}
                >
                  {/* ESQUERDA: NOME DA EMBALAGEM & QUANTIDADE */}
                  <div style={{ minWidth: 0, flex: 1, paddingRight: 8 }}>
                    <div
                      style={{
                        fontFamily: typography.fontFamily,
                        fontSize: typography.size.sm,
                        fontWeight: typography.weight.extrabold,
                        color: colors.primaryDark,
                        wordBreak: "break-word",
                      }}
                    >
                      {getPackageName(item, index)}
                    </div>

                    <div
                      style={{
                        marginTop: 2,
                        fontFamily: typography.fontFamily,
                        fontSize: typography.size.xs,
                        color: colors.textSecondary,
                      }}
                    >
                      {getPackageAmount(item, medication.medicationType)}
                    </div>
                  </div>

                  {/* DIREITA: BOTÕES DE AÇÃO TOTALMENTE ENCOSTADOS À DIREITA DO CARD */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexShrink: 0,
                      marginLeft: "auto",
                    }}
                  >
                    {!isAvailableStatus && (
                      <Badge variant={status.variant}>{status.label}</Badge>
                    )}

                    {canEditPackage(item) && (
                      <button
                        type="button"
                        onClick={() => onEditPackage?.(item)}
                        style={{
                          border: `1px solid ${colors.border}`,
                          borderRadius: 999,
                          background: colors.surface,
                          padding: "6px 11px",
                          fontFamily: typography.fontFamily,
                          fontSize: typography.size.xs,
                          fontWeight: typography.weight.bold,
                          color: colors.primaryDark,
                          cursor: "pointer",
                        }}
                      >
                        Editar
                      </button>
                    )}

                    {normalizeText(item?.status) !== "em_uso" && (
                      <button
                        type="button"
                        onClick={() => id && onUpdatePackageStatus?.(id, "START")}
                        style={{
                          border: "none",
                          borderRadius: 999,
                          background: "#F0FAF3",
                          padding: "6px 11px",
                          fontFamily: typography.fontFamily,
                          fontSize: typography.size.xs,
                          fontWeight: typography.weight.bold,
                          color: "#2F8A57",
                          cursor: "pointer",
                        }}
                      >
                        Em uso
                      </button>
                    )}

                    {normalizeText(item?.status) === "em_uso" && (
                      <button
                        type="button"
                        onClick={() => id && onUpdatePackageStatus?.(id, "STOP")}
                        style={{
                          border: "none",
                          borderRadius: 999,
                          background: "#FFF5E8",
                          padding: "6px 11px",
                          fontFamily: typography.fontFamily,
                          fontSize: typography.size.xs,
                          fontWeight: typography.weight.bold,
                          color: "#B66A00",
                          cursor: "pointer",
                        }}
                      >
                        Pausar uso
                      </button>
                    )}

                    {canDeletePackage(item) && (
                      <button
                        type="button"
                        onClick={() => {
                          if (!id) return;

                          const confirmed = window.confirm(
                            "Deseja excluir esta embalagem?"
                          );

                          if (confirmed) {
                            onDeletePackage?.(id);
                          }
                        }}
                        style={{
                          border: "none",
                          borderRadius: 999,
                          background: "#FFF1F1",
                          padding: "6px 11px",
                          fontFamily: typography.fontFamily,
                          fontSize: typography.size.xs,
                          fontWeight: typography.weight.bold,
                          color: "#C23B3B",
                          cursor: "pointer",
                        }}
                      >
                        Excluir
                      </button>
                    )}
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}